import type { BreadcrumbItem } from "@/lib/storyblok/api";
import type { LocationUrlData, ParsedAddress } from "@/lib/location";
import type {
  SchemaWebPage,
  SchemaCity,
  SchemaPlace,
  SchemaPOI,
  SchemaParkingFacilityEntity,
  POIType,
  SchemaFAQPage,
  SchemaQuestion,
  SchemaBreadcrumbList,
  SchemaBreadcrumbItem,
  SchemaItemList,
  SchemaParkingFacility,
  SchemaTouristAttraction,
  SchemaListItem,
  SchemaOpeningHoursSpecification,
  SchemaLocationFeatureSpecification,
  SchemaArticle,
  SchemaArticleItemList,
  SchemaArticleItemListEntity,
  SchemaArticleEntity,
  SchemaOrganization,
} from "./types";

export function getPOITypeFromName(name: string): POIType {
  const lowerName = name.toLowerCase();

  if (lowerName.includes("stadium") || lowerName.includes("arena")) {
    return "StadiumOrArena";
  }

  if (lowerName.includes("museum")) {
    return "Museum";
  }

  if (lowerName.includes("theater") || lowerName.includes("theatre")) {
    return "PerformingArtsTheater";
  }

  return "TouristAttraction";
}

export interface CitySchemaInput {
  cityName: string;
  locationData: LocationUrlData;
  pageUrl: string;
  description?: string;
}

export interface NeighborhoodSchemaInput {
  neighborhoodName: string;
  cityName?: string;
  locationData: LocationUrlData;
  pageUrl: string;
  description?: string;
}

export interface DestinationSchemaInput {
  poiName: string;
  poiType?: POIType;
  cityName?: string;
  locationData: LocationUrlData;
  pageUrl: string;
  description?: string;
}

export interface ParkingLotSchemaInput {
  name: string;
  image?: string;
  description?: string;
  address?: ParsedAddress;
  locationId?: string;
  amenities?: string[];
  openingHours?: Array<{ day: string; hours: string }>;
  pageUrl: string;
}

export interface FAQSchemaInput {
  items: Array<{
    question: string;
    answer: string;
  }>;
}

export interface BreadcrumbSchemaInput {
  items: BreadcrumbItem[];
  baseUrl: string;
}

export interface ParkingListInput {
  items: Array<{
    name: string;
    url: string;
    address?: string;
  }>;
}

function extractAddressFromName(name: string): string | null {
  const streetPattern = /(\d+\s+[NSEW]\.?\s+\d+(?:st|nd|rd|th)\s+(?:St|Ave|Blvd|Dr|Rd|Ln|Way)\.?)/i;
  const match = name.match(streetPattern);
  if (match?.[1]) return match[1];

  const simplePattern = /(\d+\s+[\w\s.]+(?:St|Street|Ave|Avenue|Blvd|Boulevard|Dr|Drive|Rd|Road|Ln|Lane|Way)\.?)/i;
  const simpleMatch = name.match(simplePattern);
  if (simpleMatch?.[1]) return simpleMatch[1];

  return null;
}

export interface DestinationListInput {
  items: Array<{
    name: string;
    url: string;
  }>;
}

export function generateCityPageSchema(input: CitySchemaInput): SchemaWebPage {
  const { cityName, locationData, pageUrl, description } = input;

  const citySchema: SchemaCity = {
    "@type": "City",
    name: cityName,
  };

  if (locationData.regionCode) {
    citySchema.address = {
      "@type": "PostalAddress",
      addressLocality: cityName,
      addressRegion: locationData.regionCode.toUpperCase(),
      addressCountry: "United States",
    };
  }

  const webPageSchema: SchemaWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: cityName,
    url: pageUrl,
    mainEntity: citySchema,
  };

  if (description) {
    webPageSchema.description = description;
  }

  return webPageSchema;
}

export function generateNeighborhoodPageSchema(
  input: NeighborhoodSchemaInput,
): SchemaWebPage {
  const { neighborhoodName, cityName, locationData, pageUrl, description } =
    input;

  const placeSchema: SchemaPlace = {
    "@type": "Place",
    name: neighborhoodName,
  };

  if (description) {
    placeSchema.description = description;
  }

  if (locationData.regionCode) {
    placeSchema.address = {
      "@type": "PostalAddress",
      addressLocality: cityName || neighborhoodName,
      addressRegion: locationData.regionCode.toUpperCase(),
      addressCountry: "United States",
    };
  }

  const baseUrl = process.env.NEXT_PUBLIC_DOMAIN || "https://parkmobile.io";

  const webPageSchema: SchemaWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    name: neighborhoodName,
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${baseUrl}/#website`,
    },
    mainEntity: placeSchema,
  };

  if (description) {
    webPageSchema.description = description;
  }

  return webPageSchema;
}

export function generateDestinationPageSchema(
  input: DestinationSchemaInput,
): SchemaWebPage {
  const {
    poiName,
    poiType = "TouristAttraction",
    cityName,
    locationData,
    pageUrl,
    description,
  } = input;

  const poiSchema: SchemaPOI = {
    "@type": poiType,
    name: poiName,
  };

  if (description) {
    poiSchema.description = description;
  }

  if (locationData.regionCode) {
    poiSchema.address = {
      "@type": "PostalAddress",
      addressLocality: cityName || undefined,
      addressRegion: locationData.regionCode.toUpperCase(),
      addressCountry: "United States",
    };
  }

  const baseUrl = pageUrl.split("/parking/")[0] || "";

  const webPageSchema: SchemaWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    name: poiName,
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${baseUrl}/#website`,
    },
    mainEntity: poiSchema,
  };

  if (description) {
    webPageSchema.description = description;
  }

  return webPageSchema;
}

function parseTimeRange(
  hoursString: string,
): { opens: string; closes: string } | null {
  const match = hoursString.match(
    /(\d{1,2}(?::\d{2})?\s*(?:AM|PM)?)\s*[–-]\s*(\d{1,2}(?::\d{2})?\s*(?:AM|PM)?)/i,
  );
  if (!match || !match[1] || !match[2]) return null;

  const parseTime = (timeStr: string): string => {
    const cleaned = timeStr.trim().toUpperCase();
    const timeMatch = cleaned.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/);
    if (!timeMatch || !timeMatch[1]) return "00:00";

    let hours = parseInt(timeMatch[1], 10);
    const minutes = timeMatch[2] || "00";
    const period = timeMatch[3];

    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  return {
    opens: parseTime(match[1]),
    closes: parseTime(match[2]),
  };
}

const dayOfWeekMap: Record<string, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export function generateParkingLotPageSchema(
  input: ParkingLotSchemaInput,
): SchemaWebPage {
  const {
    name,
    image,
    description,
    address,
    locationId,
    amenities,
    openingHours,
    pageUrl,
  } = input;

  const parkingSchema: SchemaParkingFacilityEntity = {
    "@type": ["ParkingFacility", "LocalBusiness"],
    name,
  };

  if (image) {
    parkingSchema.image = image;
  }

  if (description) {
    parkingSchema.description = description;
  }

  if (address) {
    parkingSchema.address = {
      "@type": "PostalAddress",
      streetAddress: address.streetAddress,
      addressLocality: address.addressLocality,
      addressRegion: address.addressRegion,
      postalCode: address.postalCode,
      addressCountry: address.addressCountry,
    };
  }

  if (openingHours && openingHours.length > 0) {
    const hoursSpecs: SchemaOpeningHoursSpecification[] = [];

    for (const { day, hours } of openingHours) {
      const dayKey = day.toLowerCase();
      const schemaDay = dayOfWeekMap[dayKey];
      if (!schemaDay) continue;

      const times = parseTimeRange(hours);
      if (times) {
        hoursSpecs.push({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: schemaDay,
          opens: times.opens,
          closes: times.closes,
        });
      }
    }

    if (hoursSpecs.length > 0) {
      parkingSchema.openingHoursSpecification = hoursSpecs;
    }
  }

  if (amenities && amenities.length > 0) {
    parkingSchema.amenityFeature = amenities.map((amenity) => ({
      "@type": "LocationFeatureSpecification" as const,
      name: amenity,
    }));
  }

  if (locationId) {
    parkingSchema.potentialAction = {
      "@type": "ReserveAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `https://app.parkmobile.io/reservation/${locationId}`,
        httpMethod: "GET",
      },
    };
  }

  const baseUrl = pageUrl.split("/parking/")[0] || "";

  const webPageSchema: SchemaWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    name,
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${baseUrl}/#website`,
    },
    mainEntity: parkingSchema,
  };

  if (description) {
    webPageSchema.description = description;
  }

  return webPageSchema;
}

export function generateFAQPageSchema(input: FAQSchemaInput): SchemaFAQPage {
  const questions: SchemaQuestion[] = input.items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  }));

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions,
  };
}

export function generateBreadcrumbListSchema(
  input: BreadcrumbSchemaInput,
): SchemaBreadcrumbList {
  const { items, baseUrl } = input;

  // Filter items for schema.org:
  // - Include items with URLs (they become clickable breadcrumbs with item/@id)
  // - Include the last item (current page) even without URL
  // - Skip intermediate items without URLs (they only appear in UI, not in schema)
  const schemaItems = items.filter((item, index) => {
    const isLast = index === items.length - 1;
    return item.url || isLast;
  });

  const itemListElement: SchemaBreadcrumbItem[] = schemaItems.map(
    (item, index) => {
      const isLast = index === schemaItems.length - 1;
      const breadcrumbItem: SchemaBreadcrumbItem = {
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
      };

      // Add item URL for all items except the last one (current page)
      if (!isLast && item.url) {
        breadcrumbItem.item = {
          "@type": "WebPage",
          "@id": `${baseUrl}${item.url}`,
        };
      }

      return breadcrumbItem;
    },
  );

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
}

export function generateParkingItemListSchema(
  input: ParkingListInput,
): SchemaItemList<SchemaParkingFacility> {
  const itemListElement: SchemaListItem<SchemaParkingFacility>[] =
    input.items.map((item, index) => {
      const extractedAddress = extractAddressFromName(item.name);
      const streetAddress = item.address || extractedAddress;

      const parkingItem: SchemaParkingFacility = {
        "@type": ["ParkingFacility", "LocalBusiness"],
        name: item.name,
        url: item.url,
        address: {
          "@type": "PostalAddress",
          addressCountry: "United States",
          ...(streetAddress && { streetAddress }),
        },
      };

      return {
        "@type": "ListItem",
        position: index + 1,
        item: parkingItem,
      };
    });

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement,
  };
}

export function generateDestinationItemListSchema(
  input: DestinationListInput,
): SchemaItemList<SchemaTouristAttraction> {
  const itemListElement: SchemaListItem<SchemaTouristAttraction>[] =
    input.items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": getPOITypeFromName(item.name),
        name: item.name,
        url: item.url,
      },
    }));

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement,
  };
}

export interface GenericWebPageSchemaInput {
  name: string;
  pageUrl: string;
  description?: string;
  mainEntity?: SchemaWebPage["mainEntity"];
}

export function generateGenericWebPageSchema(
  input: GenericWebPageSchemaInput,
): SchemaWebPage {
  const { name, pageUrl, description, mainEntity } = input;

  const baseUrl =
    pageUrl.split("/about-us")[0] ||
    pageUrl.split("/parking/")[0] ||
    pageUrl.split("/")[0] + "//" + pageUrl.split("/")[2];

  const webPageSchema: SchemaWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    name,
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${baseUrl}/#website`,
    },
  };

  if (description) {
    webPageSchema.description = description;
  }

  if (mainEntity) {
    webPageSchema.mainEntity = mainEntity;
  }

  return webPageSchema;
}

export interface ArticleListSchemaInput {
  items: Array<{
    name: string;
    url: string;
    datePublished?: string;
    description?: string;
  }>;
}

export function generateArticleListSchema(
  input: ArticleListSchemaInput,
): SchemaArticleItemList {
  const itemListElement: SchemaListItem<SchemaArticle>[] = input.items.map(
    (item, index) => {
      const article: SchemaArticle = {
        "@type": "Article",
        name: item.name,
        url: item.url,
      };

      if (item.datePublished) {
        article.datePublished = item.datePublished;
      }

      if (item.description) {
        article.description = item.description;
      }

      return {
        "@type": "ListItem",
        position: index + 1,
        item: article,
      };
    },
  );

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement,
  };
}

/** Generate ItemList without @context for use as mainEntity inside WebPage */
export function generateArticleListEntitySchema(
  input: ArticleListSchemaInput,
): SchemaArticleItemListEntity {
  const itemListElement: SchemaListItem<SchemaArticle>[] = input.items.map(
    (item, index) => {
      const article: SchemaArticle = {
        "@type": "Article",
        name: item.name,
        url: item.url,
      };

      if (item.datePublished) {
        article.datePublished = item.datePublished;
      }

      if (item.description) {
        article.description = item.description;
      }

      return {
        "@type": "ListItem",
        position: index + 1,
        item: article,
      };
    },
  );

  return {
    "@type": "ItemList",
    itemListElement,
  };
}

export interface ArticleEntitySchemaInput {
  headline: string;
  description?: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  articleBody?: string;
}

export function generateArticleEntitySchema(
  input: ArticleEntitySchemaInput,
): SchemaArticleEntity {
  const {
    headline,
    description,
    datePublished,
    dateModified,
    image,
    articleBody,
  } = input;

  const organization: SchemaOrganization = {
    "@type": "Organization",
    name: "ParkMobile, LLC",
  };

  const articleSchema: SchemaArticleEntity = {
    "@type": "Article",
    headline,
    author: organization,
    publisher: organization,
    articleSection: "Newsroom",
  };

  if (description) {
    articleSchema.description = description;
  }

  if (datePublished) {
    articleSchema.datePublished = datePublished;
  }

  if (dateModified) {
    articleSchema.dateModified = dateModified;
  }

  if (image) {
    articleSchema.image = image;
  }

  if (articleBody) {
    articleSchema.articleBody = articleBody;
  }

  return articleSchema;
}
